import {
  ExpandablePanel,
  SectionEditor,
  VerticalProgressBarCirclesWithText,
} from "@truevault/aquilo";
import React, { useCallback, useState } from "react";
import { useUpdateNoticeApi } from "../../../../components/privacyCenter/EditPrivacyCenter";
import { PrivacyCenterNoticeDto } from "../../../../../common/service/server/dto/PrivacyCenterDto";
import { Form } from "react-final-form";
import _ from "lodash";

export function BiometricIdentifiersEditor({
  organizationId,
  privacyCenterId,
  refreshNotice,
  draftNotice,
}: {
  organizationId: string;
  privacyCenterId: string;
  refreshNotice: () => Promise<void>;
  draftNotice: PrivacyCenterNoticeDto;
}) {
  const biometricIdentifiersSection = draftNotice.sections.find(
    (section) => section.anchor === "colorado-biometric-identifiers-policy",
  );

  const [updatedContent, setUpdatedContent] = useState<string>(biometricIdentifiersSection.content);
  const { updateNotice } = useUpdateNoticeApi();

  const debouncedSave = useCallback(
    _.debounce(async (newContent: string) => {
      let updateNoticeBody = {
        organizationId: organizationId,
        privacyCenterId: privacyCenterId,
        noticeId: draftNotice.publicId,
        updateNoticeBody: {
          sections: [{ id: biometricIdentifiersSection.id, content: newContent }],
        },
      };

      await updateNotice(updateNoticeBody);

      await refreshNotice();
    }, 750),
    [],
  );

  const onContentChange = useCallback(
    (newContent: string) => {
      setUpdatedContent(newContent);
      debouncedSave(newContent);
    },
    [debouncedSave],
  );

  return (
    <>
      <div style={{ maxWidth: "764px" }}>
        <div className="tw-boundary">
          <ExpandablePanel
            title="Here are the requirements for what you must include in the Biometric Identifiers Policy:"
            titleFontSize="base"
            widthClass="w-full"
          >
            <div className="mt-6">
              <VerticalProgressBarCirclesWithText
                steps={[
                  {
                    name: "State that a biometric identifier is being collected",
                    status: "complete",
                  },
                  {
                    name: "Describe the specific purpose for which it is collected",
                    status: "complete",
                  },
                  {
                    name: "Disclose the length of time that it will be retained",
                    status: "complete",
                  },
                  {
                    name: "Say whether it will be disclosed to a processor (e.g., a vendor) and, if so, the specific purpose for doing so",
                    description:
                      "Include a link to a policy that contains the following information: a protocol for responding to security incidents that may compromise biometric identifiers or biometric data, guidelines for automatically deleting biometric identifiers at certain points (e.g., when the initial purpose for collecting the data has been satisfied)",
                    status: "complete",
                  },
                ]}
              />
            </div>
          </ExpandablePanel>
        </div>

        {/* Important: make sure the actual section editor is outside of a tw-boundary ancestor in the DOM */}
        <div style={{ marginTop: "48px" }}>
          <Form
            onSubmit={() => {}}
            render={(_formRenderProps) => (
              <SectionEditor
                hideNavigationCheckbox={true}
                hideSideControls={true}
                addToNavigation={false}
                header={biometricIdentifiersSection.header}
                content={updatedContent}
                locked={false}
                placeholder={
                  biometricIdentifiersSection.placeholder ||
                  "Enter your biometric identifiers policy content here..."
                }
                onPositionUp={() => {}}
                onPositionDown={() => {}}
                moveDisabled={true}
                moveUpDisabled={true}
                moveDownDisabled={true}
                deleteDisabled={true}
                onShowInNavigationCheckboxToggle={() => {}}
                onDelete={() => {}}
                onInputChange={({ content }) => {
                  onContentChange(content);
                }}
                onUnlock={() => {}}
                enablePrivacyCenterToolbar={true}
              />
            )}
          />
        </div>
      </div>
    </>
  );
}
